import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    useTheme,
    Alert,
    Link,
    InputAdornment,
    IconButton,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { GoogleSignInButton, GoogleSignInDivider } from '../components/auth/GoogleSignInButton';

const API_URL = 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

const LoginPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [infoMessage, setInfoMessage] = useState('');

    // Check for auth message (e.g., session expired)
    useEffect(() => {
        const authMessage = sessionStorage.getItem('authMessage');
        if (authMessage) {
            setInfoMessage(authMessage);
            sessionStorage.removeItem('authMessage');
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setInfoMessage('');
        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Store tokens and user info
                localStorage.setItem('accessToken', data.tokens.accessToken);
                localStorage.setItem('idToken', data.tokens.idToken);
                localStorage.setItem('refreshToken', data.tokens.refreshToken);
                localStorage.setItem('user', JSON.stringify(data.user));

                // Redirect based on role
                const role = data.user?.role;
                if (role === 'admin' || role === 'super_admin') {
                    navigate('/admin');
                } else {
                    const redirect = searchParams.get('redirect');
                    navigate(redirect ? decodeURIComponent(redirect) : '/home');
                }
            } else {
                setError(data.error || 'Login failed');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
            }}
        >
            <Container maxWidth="sm">
                <Card sx={{ borderRadius: '16px' }}>
                    <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
    <Typography
        variant="h4"
        onClick={() => navigate('/')}
        sx={{
            fontWeight: 700,
            color: '#2D6E6A',
            cursor: 'pointer',
            fontFamily: 'Poppins',
            '&:hover': { opacity: 0.8 },
        }}
    >
        PERCORO
    </Typography>
</Box>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    border: `3px solid ${theme.palette.primary.main}`,
                                    backgroundColor: 'transparent',
                                    mb: 2,
                                }}
                            >
                                <LockOutlinedIcon sx={{ fontSize: '2.5rem', color: theme.palette.primary.main }} />
                            </Box>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                Welcome Back
                            </Typography>
                            <Typography sx={{ color: 'text.secondary' }}>
                                Sign in to continue your journey
                            </Typography>
                        </Box>

                        {infoMessage && (
                            <Alert severity="info" sx={{ mb: 3, borderRadius: '8px' }}>
                                {infoMessage}
                            </Alert>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                                {error}
                                {error.toLowerCase().includes('verify') && email && (
                                    <Box sx={{ mt: 1 }}>
                                        <a href={`/confirm-signup?email=${encodeURIComponent(email)}`}
                                           style={{ color: '#2D6E6A', fontWeight: 600 }}>
                                            Verify your email now
                                        </a>
                                    </Box>
                                )}
                            </Alert>
                        )}

                                                <GoogleSignInButton />
                        <GoogleSignInDivider />
<form onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                sx={{ mb: 3 }}
                            />

                            <TextField
                                fullWidth
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                sx={{ mb: 1 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box sx={{ textAlign: 'right', mb: 3 }}>
                                <Link
                                    href="/forgot-password"
                                    sx={{
                                        fontSize: '0.9rem',
                                        color: theme.palette.primary.main,
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    Forgot Password?
                                </Link>
                            </Box>

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={loading}
                                sx={{
                                    py: 1.5,
                                    borderRadius: '30px',
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                }}
                            >
                                {loading ? 'Signing In...' : 'Sign In'}
                            </Button>
                        </form>

                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Typography sx={{ color: 'text.secondary' }}>
                                Don't have an account?{' '}
                                <Link
                                    href="/signup"
                                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    Sign Up
                                </Link>
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default LoginPage;
